import type { IBaseAnalyticsService } from "./base-analytics.service";
import type {
  AnalyticsEvent,
  DefaultEventParams,
} from "@/modules/analytics/types";
import { AnalyticsServiceProviders } from "@/modules/analytics/types";
import type { OrganizationTierTypes } from "@/modules/common/types";
import type { DecodedToken } from "@/modules/common/context/auth/auth.context";

export class AnalyticsService {
  private services: Record<AnalyticsServiceProviders, IBaseAnalyticsService> =
    {} as any;

  constructor(
    private readonly tier: OrganizationTierTypes,
    private readonly getDefaults: () => Omit<DefaultEventParams, "tier">
  ) {}

  register(key: AnalyticsServiceProviders, svc: IBaseAnalyticsService) {
    this.services[key] = svc;
  }

  private getTargetServices(
    targets:
      | AnalyticsServiceProviders
      | AnalyticsServiceProviders[] = AnalyticsServiceProviders.ALL
  ): IBaseAnalyticsService[] {
    const list = Array.isArray(targets) ? targets : [targets];
    return (
      Object.entries(this.services) as [
        AnalyticsServiceProviders,
        IBaseAnalyticsService
      ][]
    )
      .filter(
        ([key]) =>
          list.includes(AnalyticsServiceProviders.ALL) || list.includes(key)
      )
      .map(([, svc]) => svc);
  }

  async initAll(
    targets:
      | AnalyticsServiceProviders
      | AnalyticsServiceProviders[] = AnalyticsServiceProviders.ALL
  ) {
    const services = this.getTargetServices(targets);
    await Promise.all(services.map((s) => s.init()));
  }

  async identify(
    user: DecodedToken,
    targets:
      | AnalyticsServiceProviders
      | AnalyticsServiceProviders[] = AnalyticsServiceProviders.ALL
  ) {
    this.getTargetServices(targets).forEach((s) => s.identify(user));
  }

  track<CP = Record<string, unknown>>(event: AnalyticsEvent<CP>) {
    const defaults = this.getDefaults();
    const payload = {
      ...event.customParams,
      tier: this.tier,
      device: defaults.device,
      userUid: defaults.userUid,
    };

    this.getTargetServices(event.services).forEach((s) =>
      s.track(event.name, payload)
    );
  }

  pageView(event: AnalyticsEvent) {
    const defaults = this.getDefaults();
    const payload = {
      ...event.customParams,
      tier: this.tier,
      device: defaults.device,
      userUid: defaults.userUid,
    };
    this.getTargetServices(event.services).forEach((s) =>
      s.pageView(event.name, payload)
    );
  }

  unidentifyAll(
    targets:
      | AnalyticsServiceProviders
      | AnalyticsServiceProviders[] = AnalyticsServiceProviders.ALL
  ) {
    this.getTargetServices(targets).forEach((s) => s.unidentify());
  }

  async setUserAttributes(
    attributes: Record<string, unknown>,
    targets:
      | AnalyticsServiceProviders
      | AnalyticsServiceProviders[] = AnalyticsServiceProviders.ALL
  ) {
    this.getTargetServices(targets).forEach((s) => {
      if (s.setUserAttributes) {
        s.setUserAttributes(attributes);
      }
    });
  }
}
